import React, {useEffect, useState} from 'react';
import {useSelector, shallowEqual} from 'react-redux';
import {labels, negativeNumberFormattedText} from '../../../util/utils';
import styled from 'styled-components';
import MaterialityFetch from '../Materiality/MaterialityFetch';
import {useUserPreferences} from '../../../util/customHooks';

const ActivitySummary = (props) => {
	const {documentId} = props;
	const limitATEPercent = 49;
	const lowerTEPercent = 50;
	const limitBTEPercent = 51;
	const limitCTEPercent = 74;
	const upperTEPercent = 75;
	const limitDTEPercent = 76;
	const materiality = useSelector((state) => Object.values(state.materiality.byId), shallowEqual);
	const userPreferences = useUserPreferences();
	const [currentActivitySummary, setCurrentActivitySummary] = useState({});
	const [priorActivitySummary, setPriorActivitySummary] = useState({});
	const [preferencesJson, setPreferencesJson] = useState(null);

	useEffect(() => {
		setPreferencesJson(JSON.parse(userPreferences.userPreferencesJson));
	}, []);

	useEffect(() => {
		if (Object.keys(materiality).length > 0) {
			materiality.map((item) => {
				let result = calculateResults(item);
				if (item.isCurrent) {
					setCurrentActivitySummary({
						currency: result.Currency,
						currentPM: result.PMAmount,
						showCurrentPMPercent: result.ShowPMPercent,
						currentPMPercent: result.PMPercent,
						currentTE: result.TEAmount,
						currentTEPercent: result.TEPercent,
						currentSAD: result.SADNominalAmount,
						currentSADPercent: result.SADPercent
					});
				} else if (item.isPriorYear) {
					setPriorActivitySummary({
						currency: result.Currency,
						priorPM: result.PMAmount,
						showPriorPMPercent: result.ShowPMPercent,
						priorPMPercent: result.PMPercent,
						priorTE: result.TEAmount,
						priorTEPercent: result.TEPercent,
						priorSAD: result.SADNominalAmount,
						priorSADPercent: result.SADPercent
					});
				}
			});
		}
	}, [materiality]);

	const calculateResults = (item) => {
		let PMPercent;
		let TEPercent;
		let SADPercent;
		let pmAmount = item.pmAmount ? Math.trunc(item.pmAmount) : item.pmAmount;
		let divider =
			item.basisAdjustedAmount && item.basisAdjustedAmount > 0
				? item.basisAdjustedAmount
				: item.basisForecastAmount && item.basisForecastAmount > 0
				? item.basisForecastAmount
				: 0;
		if (divider > 0) {
			PMPercent = ((item.pmAmount / divider) * 100).toFixed(1);
		}
		if (item.pmAmount && item.pmAmount !== 0) {
			if (item.teAmount && item.teAmount !== 0) {
				TEPercent = Math.round((item.teAmount / item.pmAmount) * 100);
				if (TEPercent >= limitATEPercent && TEPercent <= limitBTEPercent) {
					TEPercent = lowerTEPercent;
				} else if (TEPercent >= limitCTEPercent && TEPercent <= limitDTEPercent) {
					TEPercent = upperTEPercent;
				}
			}
			SADPercent = Math.round((item.sadNominalAmount / item.pmAmount) * 100);
		}

		return {
			Currency: item.helixModel?.currencyFormat,
			PMAmount: pmAmount,
			ShowPMPercent: divider !== 0,
			PMPercent: PMPercent,
			TEAmount: item.teAmount ? Math.trunc(item.teAmount) : item.teAmount,
			TEPercent: TEPercent,
			SADNominalAmount: item.sadNominalAmount ? Math.trunc(item.sadNominalAmount) : item.sadNominalAmount,
			SADPercent: SADPercent
		};
	};

	return (
		<MaterialityFetch documentId={documentId}>
			<MaterialityActivitySummary>
				<section className="materialityactivity-summary">
					<section className="current-materialityactivity">
						<span
							className={
								currentActivitySummary.currency ? 'materialityactivity-label' : 'materialityactivity-label prior-label'
							}
							title={labels.currentLabel}
						>
							{labels.currentLabel}
						</span>
						<span className={currentActivitySummary.currency ? 'materialityactivity-currency' : ''}>
							{currentActivitySummary.currency ? '(' + currentActivitySummary.currency + ')' : ''}
						</span>
						<span className="materialityactivity-label">{labels.PMLabel}</span>
						<span className="materialityactivity-value">
							{currentActivitySummary.currentPM || currentActivitySummary.currentPM === 0
								? negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										currentActivitySummary.currentPM
								  )
								: '-'}
						</span>
						<span className="materialityactivity-percent">
							{currentActivitySummary.showCurrentPMPercent
								? '(' +
								  negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										currentActivitySummary.currentPMPercent
								  ) +
								  '%)'
								: '-'}
						</span>
						<span className="materialityactivity-label">{labels.TELabel}</span>
						<span className="materialityactivity-value">
							{currentActivitySummary.currentTE || currentActivitySummary.currentTE === 0
								? negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										currentActivitySummary.currentTE
								  )
								: '-'}
						</span>
						<span className="materialityactivity-percent">
							{currentActivitySummary.currentTEPercent ? '(' + currentActivitySummary.currentTEPercent + '%)' : '-'}
						</span>
						<span className="materialityactivity-label">{labels.SADLabel}</span>
						<span className="materialityactivity-value">
							{currentActivitySummary.currentSAD || currentActivitySummary.currentSAD === 0
								? negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										currentActivitySummary.currentSAD
								  )
								: '-'}
						</span>
						<span className="materialityactivity-percent">
							{currentActivitySummary.currentSADPercent
								? '(' +
								  negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										currentActivitySummary.currentSADPercent
								  ) +
								  '%)'
								: '-'}
						</span>
					</section>
					<section className="prior-materialityactivity">
						<span className="materialityactivity-label prior-label" title={labels.PriorLabel}>
							{labels.PriorLabel}
						</span>
						<span className="materialityactivity-label">{labels.PMLabel}</span>
						<span className="materialityactivity-value">
							{priorActivitySummary.priorPM || priorActivitySummary.priorPM === 0
								? negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										priorActivitySummary.priorPM
								  )
								: '-'}
						</span>
						<span className="materialityactivity-percent">
							{priorActivitySummary.showPriorPMPercent
								? '(' +
								  negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										priorActivitySummary.priorPMPercent
								  ) +
								  '%)'
								: '-'}
						</span>
						<span className="materialityactivity-label">{labels.TELabel}</span>
						<span className="materialityactivity-value">
							{priorActivitySummary.priorTE || priorActivitySummary.priorTE === 0
								? negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										priorActivitySummary.priorTE
								  )
								: '-'}
						</span>
						<span className="materialityactivity-percent">
							{priorActivitySummary.priorTEPercent ? '(' + priorActivitySummary.priorTEPercent + '%)' : '-'}
						</span>
						<span className="materialityactivity-label">{labels.SADLabel}</span>
						<span className="materialityactivity-value">
							{priorActivitySummary.priorSAD || priorActivitySummary.priorSAD === 0
								? negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										priorActivitySummary.priorSAD
								  )
								: '-'}
						</span>
						<span className="materialityactivity-percent">
							{priorActivitySummary.priorSADPercent
								? '(' +
								  negativeNumberFormattedText(
										userPreferences?.numberFormat,
										preferencesJson?.negativenumberformat ?? 0,
										priorActivitySummary.priorSADPercent
								  ) +
								  '%)'
								: '-'}
						</span>
					</section>
				</section>
			</MaterialityActivitySummary>
		</MaterialityFetch>
	);
};

export default ActivitySummary;

const MaterialityActivitySummary = styled.section`
	padding: var(--px-30) var(--px-155);
	.materialityactivity-title {
		display: block;
		font-size: var(--px-20);
		font-weight: 300;
		margin-bottom: var(--px-20);
	}
	.materialityactivity-summary {
		width: 100%;
		box-shadow: 0 0 var(--px-5) 0 var(--neutrals-300);
		border-radius: var(--px-10);
		background: var(--neutrals-100);
		text-align: center;
		.current-materialityactivity {
			display: inline-block;
			width: 50%;
			padding: var(--px-20) var(--px-30);
			box-shadow: 0 0 var(--px-5) 0 var(--neutrals-300);
			border-radius: var(--px-10);
			background: var(--neutrals-00white);
			@media only screen and (min-width: 1280px) and (max-width: 1366px) {
				padding: var(--px-15) var(--px-10);
			}
			span {
				display: inline-block;
				font-size: var(--px-14);
				@media only screen and (min-width: 1280px) and (max-width: 1366px) {
					font-size: var(--px-12);
				}
			}
			.materialityactivity-label {
				font-weight: bold;
				padding-right: var(--px-5);
				max-width: var(--px-65);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				vertical-align: top;
				@media only screen and (min-width: 1280px) and (max-width: 1366px) {
					max-width: var(--px-55);
				}
				&.prior-label {
					padding-right: var(--px-10);
					margin-right: var(--px-10);
					border-right: var(--px-1) solid var(--neutrals-300);
				}
			}
			.materialityactivity-currency {
				font-weight: 300;
				padding-right: var(--px-10);
				margin-right: var(--px-10);
				border-right: var(--px-1) solid var(--neutrals-300);
			}

			.materialityactivity-percent {
				font-weight: 300;
				padding-right: var(--px-10);
			}
			.materialityactivity-value {
				font-weight: bold;
				padding-right: var(--px-5);
				color: var(--green-500);
			}
		}
		.prior-materialityactivity {
			display: inline-block;
			width: 50%;
			padding: var(--px-20) var(--px-30);
			text-align: center;
			@media only screen and (min-width: 1280px) and (max-width: 1366px) {
				padding: var(--px-15) var(--px-10);
			}
			span {
				display: inline-block;
				font-size: var(--px-14);
				@media only screen and (min-width: 1280px) and (max-width: 1366px) {
					font-size: var(--px-12);
				}
			}
			.materialityactivity-label {
				font-weight: bold;
				padding-right: var(--px-5);
				max-width: var(--px-65);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				vertical-align: top;
				@media only screen and (min-width: 1280px) and (max-width: 1366px) {
					max-width: var(--px-55);
				}
				&.prior-label {
					padding-right: var(--px-10);
					margin-right: var(--px-10);
					border-right: var(--px-1) solid var(--neutrals-300);
				}
			}
			.materialityactivity-percent {
				font-weight: 300;
				padding-right: var(--px-10);
			}
			.materialityactivity-value {
				font-weight: bold;
				padding-right: var(--px-5);
			}
		}
	}
`;
